import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { ADMIN_EMAIL } from '../config';
import BrandSignInTokenService from '../services/brandSignInTokenService';
import { Copy, Check, Link as LinkIcon, User, Mail, Building2, Calendar, FileText, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiUrl } from '../config/api';
import { API_CONFIG } from '../config/api';
import './AdminBrandSignups.css';

function AdminBrandSignups() {
  const [brandSignups, setBrandSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLinks, setCopiedLinks] = useState({});
  const [generatingLinks, setGeneratingLinks] = useState({});
  const [notes, setNotes] = useState({});
  const [editingNotes, setEditingNotes] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [newBrandEmail, setNewBrandEmail] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    // Check if user is admin
    const auth = getAuth();
    const user = auth.currentUser;
    
    console.log('AdminBrandSignups: Checking admin access', {
      userEmail: user?.email,
      adminEmail: ADMIN_EMAIL,
      isMatch: user?.email === ADMIN_EMAIL
    });
    
    if (user && user.email === ADMIN_EMAIL) {
      setIsAdmin(true);
      loadBrandSignups();
    } else {
      setError(`Access denied. Admin access required. Your email: ${user?.email || 'Not logged in'}, Required: ${ADMIN_EMAIL}`);
      setLoading(false);
    }
  }, []);

  const loadBrandSignups = () => {
    try {
      // Query users collection for clients/brands who have signed up
      // We'll get all users and filter client/brand types
      const usersRef = collection(db, 'users');
      
      const unsubscribe = onSnapshot(usersRef, (snapshot) => {
        const signups = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Include users with userType 'client' or 'brand', or no userType (new signups)
          const userType = data.userType;
          if (!userType || userType === 'client' || userType === 'brand') {
            signups.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || (data.createdAt ? new Date(data.createdAt) : null)
            });
          }
        });
        
        // Sort by creation date (newest first)
        signups.sort((a, b) => {
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt - a.createdAt;
        });
        
        setBrandSignups(signups);
        setLoading(false);
        
        // Load saved notes
        const savedNotes = {};
        signups.forEach(signup => {
          if (signup.adminNotes) {
            savedNotes[signup.id] = signup.adminNotes;
          }
        });
        setNotes(savedNotes);
      }, (err) => {
        console.error('Error loading brand signups:', err);
        setError('Failed to load brand signups');
        setLoading(false);
      });

      return unsubscribe;
    } catch (err) {
      console.error('Error setting up brand signups listener:', err);
      setError('Failed to load brand signups');
      setLoading(false);
    }
  };

  const handleGenerateLink = async (signup) => {
    setGeneratingLinks(prev => ({ ...prev, [signup.id]: true }));
    
    try {
      const brandEmail = signup.email || signup.brandEmail || '';
      const brandName = signup.brandName || signup.name || 'Brand';
      
      if (!brandEmail) {
        toast.error('Brand email not found');
        setGeneratingLinks(prev => ({ ...prev, [signup.id]: false }));
        return;
      }

      const result = await BrandSignInTokenService.generateSignInToken(
        signup.id,
        brandEmail,
        brandName
      );

      // Copy link to clipboard
      await navigator.clipboard.writeText(result.link);
      setCopiedLinks(prev => ({ ...prev, [signup.id]: true }));
      toast.success('Sign-in link generated and copied to clipboard!');
      
      setTimeout(() => {
        setCopiedLinks(prev => {
          const newState = { ...prev };
          delete newState[signup.id];
          return newState;
        });
      }, 3000);
    } catch (err) {
      console.error('Error generating sign-in link:', err);
      toast.error('Failed to generate sign-in link');
    } finally {
      setGeneratingLinks(prev => ({ ...prev, [signup.id]: false }));
    }
  };

  const handleSaveNotes = async (signupId) => {
    try {
      const userRef = doc(db, 'users', signupId);
      await updateDoc(userRef, {
        adminNotes: notes[signupId] || ''
      });
      setEditingNotes(prev => {
        const newState = { ...prev };
        delete newState[signupId];
        return newState;
      });
      toast.success('Notes saved');
    } catch (err) {
      console.error('Error saving notes:', err);
      toast.error('Failed to save notes');
    }
  };

  const handleNotesChange = (signupId, value) => {
    setNotes(prev => ({
      ...prev,
      [signupId]: value
    }));
  };

  const handleGenerateSignupToken = async (e) => {
    e.preventDefault();
    
    if (!newBrandEmail || !newBrandName) {
      toast.error('Please fill in both email and brand name');
      return;
    }
    
    setGeneratingToken(true);
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        toast.error('You must be logged in to generate tokens');
        setGeneratingToken(false);
        return;
      }
      
      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();
      
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.GENERATE_BRAND_SIGNUP_TOKEN), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          brandEmail: newBrandEmail,
          brandName: newBrandName,
          adminEmail: user.email
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate token');
      }
      
      if (data.success) {
        // Store the generated link
        setGeneratedLink({
          url: data.signupUrl,
          email: newBrandEmail,
          name: newBrandName,
          expiresAt: data.expiresAt
        });
        // Copy link to clipboard automatically
        if (data.signupUrl) {
          await navigator.clipboard.writeText(data.signupUrl);
          setLinkCopied(true);
          toast.success('Sign-up link generated and copied to clipboard!');
        }
        // Reset form inputs but keep form open to show the link
        setNewBrandEmail('');
        setNewBrandName('');
      } else {
        throw new Error(data.error || 'Failed to generate token');
      }
    } catch (err) {
      console.error('Error generating signup token:', err);
      toast.error(err.message || 'Failed to generate sign-up token');
    } finally {
      setGeneratingToken(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-brand-signups-container">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You must be an admin to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-brand-signups-container">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading brand sign-ups...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-brand-signups-container">
        <div className="admin-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-brand-signups-container">
      <div className="admin-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h1>Brand Sign-Ups</h1>
            <p className="admin-subtitle">Manage brand sign-ups and generate sign-in links</p>
          </div>
          <button
            onClick={() => {
              console.log('Generate button clicked, showGenerateForm:', showGenerateForm);
              setShowGenerateForm(!showGenerateForm);
            }}
            className="btn-generate-signup-token"
            style={{
              padding: '12px 24px',
              backgroundColor: '#f8049c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap'
            }}
          >
            <Plus size={18} />
            {showGenerateForm ? 'Cancel' : 'Generate Brand Sign-Up Link'}
          </button>
        </div>
      </div>

      {showGenerateForm && (
        <div className="generate-signup-form" style={{
          marginBottom: '30px',
          padding: '20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
          <h3 style={{ marginTop: 0 }}>Generate Brand Sign-Up Link</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Create a sign-up link for a new brand. The link will be generated below for you to copy and send manually.
          </p>
          
          {generatedLink && (
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#e8f5e9',
              borderRadius: '8px',
              border: '1px solid #4caf50'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#2e7d32' }}>
                ✅ Link Generated Successfully!
              </h4>
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Brand:</strong> {generatedLink.name} ({generatedLink.email})
              </p>
              <p style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Expires:</strong> {generatedLink.expiresAt}
              </p>
              <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                marginTop: '15px'
              }}>
                <input
                  type="text"
                  value={generatedLink.url}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #4caf50',
                    backgroundColor: 'white',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(generatedLink.url);
                    setLinkCopied(true);
                    toast.success('Link copied to clipboard!');
                    setTimeout(() => setLinkCopied(false), 3000);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: linkCopied ? '#4caf50' : '#f8049c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  {linkCopied ? <Check size={16} /> : <Copy size={16} />}
                  {linkCopied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setGeneratedLink(null);
                  setLinkCopied(false);
                }}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Generate Another Link
              </button>
            </div>
          )}
          <form onSubmit={handleGenerateSignupToken}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Brand Email *
              </label>
              <input
                type="email"
                value={newBrandEmail}
                onChange={(e) => setNewBrandEmail(e.target.value)}
                placeholder="brand@example.com"
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Brand Name *
              </label>
              <input
                type="text"
                value={newBrandName}
                onChange={(e) => setNewBrandName(e.target.value)}
                placeholder="Brand Name"
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={generatingToken}
              style={{
                padding: '10px 20px',
                backgroundColor: generatingToken ? '#ccc' : '#f8049c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: generatingToken ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {generatingToken ? 'Generating...' : 'Generate Link & Send Email'}
            </button>
          </form>
        </div>
      )}

      <div className="brand-signups-grid">
        {brandSignups.length === 0 ? (
          <div className="no-signups">
            <p>No brand sign-ups found.</p>
          </div>
        ) : (
          brandSignups.map((signup) => (
            <div key={signup.id} className="brand-signup-card">
              <div className="brand-signup-card-header">
                <div className="brand-signup-avatar">
                  {signup.profileImage ? (
                    <img src={signup.profileImage} alt={signup.brandName || signup.name} />
                  ) : (
                    <div className="brand-signup-avatar-placeholder">
                      <Building2 size={24} />
                    </div>
                  )}
                </div>
                <div className="brand-signup-info">
                  <h3 className="brand-signup-name">{signup.brandName || signup.name || 'Unknown Brand'}</h3>
                  <div className="brand-signup-meta">
                    {signup.email && (
                      <div className="brand-signup-meta-item">
                        <Mail size={14} />
                        <span>{signup.email}</span>
                      </div>
                    )}
                    {signup.accountType && (
                      <div className="brand-signup-meta-item">
                        <User size={14} />
                        <span className="account-type-badge">{signup.accountType}</span>
                      </div>
                    )}
                    {signup.createdAt && (
                      <div className="brand-signup-meta-item">
                        <Calendar size={14} />
                        <span>{signup.createdAt.toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="brand-signup-details">
                {signup.website && (
                  <div className="brand-signup-detail-item">
                    <strong>Website:</strong> {signup.website}
                  </div>
                )}
                {signup.productService && (
                  <div className="brand-signup-detail-item">
                    <strong>Product/Service:</strong> {signup.productService}
                  </div>
                )}
                {signup.monthlyBudget && (
                  <div className="brand-signup-detail-item">
                    <strong>Monthly Budget:</strong> {signup.monthlyBudget}
                  </div>
                )}
                {signup.goals && signup.goals.length > 0 && (
                  <div className="brand-signup-detail-item">
                    <strong>Goals:</strong> {signup.goals.join(', ')}
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className="brand-signup-notes-section">
                <div className="brand-signup-notes-header">
                  <FileText size={16} />
                  <h4>Notes</h4>
                </div>
                {editingNotes[signup.id] ? (
                  <div className="brand-signup-notes-editor">
                    <textarea
                      value={notes[signup.id] || ''}
                      onChange={(e) => handleNotesChange(signup.id, e.target.value)}
                      placeholder="Add notes about this brand sign-up..."
                      className="brand-signup-notes-textarea"
                    />
                    <div className="brand-signup-notes-actions">
                      <button
                        onClick={() => handleSaveNotes(signup.id)}
                        className="btn-save-notes"
                      >
                        Save Notes
                      </button>
                      <button
                        onClick={() => {
                          setEditingNotes(prev => {
                            const newState = { ...prev };
                            delete newState[signup.id];
                            return newState;
                          });
                        }}
                        className="btn-cancel-notes"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="brand-signup-notes-display">
                    <p className="brand-signup-notes-text">
                      {notes[signup.id] || 'No notes yet. Click to add notes.'}
                    </p>
                    <button
                      onClick={() => setEditingNotes(prev => ({ ...prev, [signup.id]: true }))}
                      className="btn-edit-notes"
                    >
                      {notes[signup.id] ? 'Edit Notes' : 'Add Notes'}
                    </button>
                  </div>
                )}
                
                {/* Generate Sign In Link Button */}
                <div className="brand-signup-generate-link">
                  <button
                    onClick={() => handleGenerateLink(signup)}
                    disabled={generatingLinks[signup.id]}
                    className="btn-generate-link"
                  >
                    {generatingLinks[signup.id] ? (
                      <>
                        <div className="spinner-small"></div>
                        Generating...
                      </>
                    ) : copiedLinks[signup.id] ? (
                      <>
                        <Check size={16} />
                        Link Copied!
                      </>
                    ) : (
                      <>
                        <LinkIcon size={16} />
                        Generate Sign In Link
                      </>
                    )}
                  </button>
                  {copiedLinks[signup.id] && (
                    <p className="link-copied-message">
                      Link copied to clipboard! Send this to the brand.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminBrandSignups;

